import {
    Dialog,
    DialogTitle,
    Paper,
    Stack,
    Button,
    CircularProgress,
    DialogContent,
    DialogActions,
    Typography,
    Divider,
} from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import {
    addToTickTickList,
    categorizeIngredient,
    getUserTickTickList,
    getUserTickTickLists,
    NewTickTickTask,
    TickTick,
    TickTickList,
    TickTickTask,
    UpdateTickTickTask,
} from "../data";
import {
    IngredientSource,
    ShoppingListCategory,
    ShoppingListIngredient,
    sumSources,
    useShoppingList,
} from "../contexts/ShoppingListContext";
import {
    buildIngredientString,
    buildShoppingItem,
    buildShoppingItemInfo,
    displayQuantityValue,
    ParsedIngredient,
    parseIngredientDays,
    parseIngredientString,
} from "../utils/shoppingLists";
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import { findIndex, findLast, reorder } from "../utils/list";
import { UniqueId } from "../utils/unique";
import { capitalizeFirst } from "../textFormattings";
import PendingButton from "./base/PendingButton";
import DraggableIngredient from "./base/DraggableIngredient";

export interface IngredientCategory {
    type: "category";
    id?: number;
    name?: string | null;
}

export interface IngredientEntry {
    type: "entry";
    uniqueId: string;
    ingredient?: ShoppingListIngredient;
    task?: { t: TickTickTask; i?: ParsedIngredient } | null;
}

export type Entry = IngredientCategory | IngredientEntry;

function isCategory(entry: Entry): entry is IngredientCategory {
    return entry.type === "category";
}

function isIngredient(entry: Entry): entry is IngredientEntry {
    return entry.type === "entry";
}

export interface BuildUpdatesResult {
    newTasks: (NewTickTickTask | UpdateTickTickTask)[];
    entries: IngredientEntry[];
}

export function getChangedItems(list: { sortOrder: number }[]): number[] {
    if (list.length <= 1) return [];

    const n = list.length;
    const dp: number[] = new Array(n).fill(1); // DP-tabell för längd på LIS
    const parent: number[] = new Array(n).fill(-1); // För att hålla reda på föregående element i LIS

    // Bygg upp LIS med hjälp av dynamisk programmering
    for (let i = 1; i < n; i++) {
        for (let j = 0; j < i; j++) {
            if (list[i].sortOrder > list[j].sortOrder && dp[i] < dp[j] + 1) {
                dp[i] = dp[j] + 1;
                parent[i] = j;
            }
        }
    }

    // Hitta längden på den längsta stigande delsekvensen
    const lisLength = Math.max(...dp);

    // Hitta indexen för elementen i LIS
    const lisIndexes = new Set<number>();
    let index = dp.indexOf(lisLength);
    while (index !== -1) {
        lisIndexes.add(index);
        index = parent[index];
    }

    // Returnera de index som inte är i LIS
    const changedIndexes: number[] = [];
    for (let i = 0; i < n; i++) {
        if (!lisIndexes.has(i)) {
            changedIndexes.push(i);
        }
    }

    return changedIndexes;
}

export function buildUpdates(taskList: Entry[]): BuildUpdatesResult {
    interface SortAnchor {
        index: number;
        sortOrder: number;
    }
    const newTasks: (NewTickTickTask | UpdateTickTickTask)[] = [];
    const entries: IngredientEntry[] = [];
    const originalTasks = taskList.reduce((sum, t, index) => {
        if (t.type === "entry" && t.task) {
            sum.push({ index, sortOrder: t.task.t.sortOrder });
        }
        return sum;
    }, [] as SortAnchor[]);

    let anchorPoints: SortAnchor[] = [];
    const changedIndices = getChangedItems(originalTasks);
    if (originalTasks.length === 1) {
        anchorPoints.push(originalTasks[0]);
    } else {
        anchorPoints.push(...originalTasks);
        for (const index of changedIndices.reverse()) {
            anchorPoints.splice(index, 1);
        }
    }
    const indicesThatNeedNewSortOrder = new Set<number>(changedIndices.map(index => originalTasks[index].index));
    const beforeAnchorPoint = (anchorPoints.length === 0 ? 0 : anchorPoints[0].sortOrder) - 10_000_000;
    const afterAnchorPoint =
        (anchorPoints.length === 0 ? 0 : anchorPoints[anchorPoints.length - 1].sortOrder) + 10_000_000;
    anchorPoints.unshift({ index: -1, sortOrder: beforeAnchorPoint });
    anchorPoints.push({ index: taskList.length, sortOrder: afterAnchorPoint });

    function getSortOrder(index: number): number {
        const anchorIndex = anchorPoints.findIndex(t => index < t.index);
        const before = anchorPoints[anchorIndex - 1];
        const after = anchorPoints[anchorIndex];
        const indexLength = after.index - before.index;
        const t = (index - before.index) / indexLength;
        return Math.round(before.sortOrder + (after.sortOrder - before.sortOrder) * t);
    }

    for (let index = 0; index < taskList.length; ++index) {
        const entry = taskList[index];
        if (entry.type !== "entry") continue;

        const i = entry.ingredient;
        if (i) {
            const { item, extra, date } = buildShoppingItem(i);
            const title = item;
            const desc = extra || date?.weekDay;
            if (entry.task) {
                const sortOrder = indicesThatNeedNewSortOrder.has(index) ? getSortOrder(index) : undefined;
                if (entry.task.t.title !== title || entry.task.t.desc !== desc) {
                    entries.push(entry);
                    newTasks.push({
                        id: entry.task.t.id,
                        title,
                        desc,
                        content: desc,
                        sortOrder,
                    });
                } else if (sortOrder !== undefined) {
                    entries.push(entry);
                    newTasks.push({
                        id: entry.task.t.id,
                        sortOrder: sortOrder,
                    });
                }
            } else {
                entries.push(entry);
                newTasks.push({ title, desc, content: desc, sortOrder: getSortOrder(index) });
            }
        } else if (entry.task && indicesThatNeedNewSortOrder.has(index)) {
            entries.push(entry);
            newTasks.push({
                id: entry.task.t.id,
                sortOrder: getSortOrder(index),
            });
        }
    }

    return { newTasks, entries };
}

function Category({ name, index }: { name: string; index: number }) {
    return (
        <Draggable draggableId={name} index={index} isDragDisabled={true}>
            {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                    <div style={{ paddingTop: "7.5px", paddingBottom: "7.5px" }}>
                        <span>{name}</span>
                    </div>
                </div>
            )}
        </Draggable>
    );
}

interface IngredientProps {
    entry: IngredientEntry;
    index: number;
}

function Ingredient({ entry: e, index }: IngredientProps) {
    const isNew = e.ingredient && !e.task;
    let isModified = false;
    let title: React.ReactNode;
    let subTitle: React.ReactNode;
    if (e.ingredient) {
        const info = buildShoppingItemInfo(e.ingredient);
        if (e.task?.i) {
            isModified = true;
            const prettyName = capitalizeFirst(e.ingredient.name);
            const oldQuantity = e.task.i.quantity ? displayQuantityValue(e.task.i.quantity) : undefined;
            const newQuantity = e.ingredient.quantity ? displayQuantityValue(e.ingredient.quantity) : undefined;
            title = [
                <span key="1">{prettyName}, </span>,
                <s key="2">
                    {oldQuantity} {e.task?.i?.unit}
                </s>,
                <span key="3">
                    {" "}
                    {newQuantity} {e.ingredient.unit}
                </span>,
            ];
        } else {
            title = buildIngredientString(e.ingredient.name, e.ingredient.quantity, e.ingredient.unit);
        }

        const descText = info.distribution || info.date?.weekDay;
        if (e.task && e.task.t.desc && e.task.t.desc !== descText) {
            subTitle = [<span key="1">{descText}</span>, <br key="2"></br>, <s key="3">{e.task.t.desc}</s>];
        } else {
            subTitle = descText || "";
        }
    } else if (e.task) {
        const t = e.task.t;
        title = t.title;
        subTitle = t.desc || t.content;
    }

    const color = isNew ? "#ccffc1" : isModified ? "#fff395" : "white";
    return (
        <DraggableIngredient
            id={e.uniqueId}
            index={index}
            title={title}
            subTitle={subTitle}
            color={color}
        ></DraggableIngredient>
    );
}

interface IngredientsToBuyProps {
    entries: Entry[];
}

function IngredientsToBuy({ entries }: IngredientsToBuyProps) {
    return (
        <Droppable droppableId="list">
            {(provided, snapshot) => (
                <Stack {...provided.droppableProps} ref={provided.innerRef} direction="column" paddingTop="15px">
                    {entries.map((e, index) =>
                        e.type === "category" ? (
                            <Category
                                key={e.name || "Ingen kategori"}
                                name={e.name || "Ingen kategori"}
                                index={index}
                            ></Category>
                        ) : (
                            <Ingredient key={e.uniqueId} entry={e} index={index}></Ingredient>
                        )
                    )}
                    {provided.placeholder}
                </Stack>
            )}
        </Droppable>
    );
}

export interface TickTickDialogProps {
    open: boolean;
    onClose?: () => void;
    shoppingListToAdd: ShoppingListCategory[];
}

interface Selected {
    tickTick: TickTick;
    list: TickTickList;
}

export default function TickTickDialog({ open, onClose, shoppingListToAdd }: TickTickDialogProps) {
    const { ingredientToCategory } = useShoppingList();
    const [tickTick, setTickTick] = useState<TickTick[] | undefined>();
    const [selectedList, setSelectedList] = useState<Selected | undefined>();
    const [taskList, setTaskList] = useState<Entry[] | undefined>();
    const [updating, setUpdating] = useState<{ count: number; total: number } | undefined>();

    useEffect(() => {
        getUserTickTickLists().then(setTickTick);
    }, [setTickTick]);

    const onSelectList = (tickTick: TickTick, list: TickTickList) => {
        setTaskList(undefined);
        setSelectedList({ tickTick, list });

        getUserTickTickList(tickTick.id, list.id).then(tasks => {
            const idGen = new UniqueId();
            tasks.sort((a, b) => a.sortOrder - b.sortOrder);

            let entries: Entry[] = [];
            for (const c of shoppingListToAdd) {
                entries.push({ type: "category", id: c.id, name: c.name });
                for (const ingredient of c.items) {
                    entries.push({
                        type: "entry",
                        uniqueId: idGen.next(),
                        ingredient: { ...ingredient, sources: [...ingredient.sources] },
                    });
                }
            }

            for (const task of tasks) {
                const ingredient = parseIngredientString(task.title);
                const days = parseIngredientDays(task.desc);
                if (ingredient && ingredient.quantity && ingredient.quantity.type === "number") {
                    let handled = false;
                    for (const entry of entries) {
                        if (entry.type !== "entry" || !entry.ingredient) continue;

                        if (
                            entry.ingredient.name === ingredient.name &&
                            entry.ingredient.quantity &&
                            entry.ingredient.unit === ingredient.unit
                        ) {
                            const quantity = entry.ingredient.quantity;
                            if (quantity.type === "text" || quantity.type === "range") {
                                continue;
                            }

                            let tickTickSources: IngredientSource[];
                            let tickTickSource: IngredientSource = {
                                name: "TickTick",
                                ingredient: { ...ingredient, uniqueId: idGen.next(), scale_outcome: "scaled" },
                            };
                            if (days.length === 1) {
                                tickTickSource.day = days[0].day;
                                tickTickSources = [tickTickSource];
                            } else if (days.length >= 2) {
                                tickTickSources = days.map(day => {
                                    const ingredient = { ...tickTickSource.ingredient };
                                    if (day.quantity) {
                                        ingredient.quantity = day.quantity;
                                    }
                                    return {
                                        name: tickTickSource.name,
                                        ingredient,
                                        day: day.day,
                                    };
                                });
                            } else {
                                tickTickSources = [tickTickSource];
                            }

                            const sources = entry.ingredient.sources.concat(tickTickSources);
                            entry.ingredient.sources = sources;
                            entry.ingredient.quantity = sumSources(sources);
                            entry.task = { t: task, i: ingredient };
                            handled = true;
                        }
                    }
                    if (handled) continue;
                }
                const entry: IngredientEntry = {
                    type: "entry",
                    uniqueId: idGen.next(),
                    task: { t: task, i: ingredient },
                };

                const category = ingredientToCategory.get(ingredient?.name || task.title.toLowerCase());
                if (category) {
                    const categoryIndex = entries.findIndex(e => e.type === "category" && e.id === category.id);
                    if (categoryIndex !== -1) {
                        const nextCategoryIndex = findIndex(entries, categoryIndex + 1, isCategory);
                        if (nextCategoryIndex !== -1) {
                            entries.splice(nextCategoryIndex, 0, entry);
                            continue;
                        }
                    }
                }
                entries.push(entry);
            }

            setTaskList(entries);
        });
    };

    const onDragEnd: OnDragEndResponder = result => {
        const { source, destination } = result;
        if (!destination || !taskList) {
            return;
        }

        const lastCategory = findLast(taskList, source.index, isCategory);
        const newCategory = findLast(taskList, destination.index, isCategory) as IngredientCategory;
        if (lastCategory !== newCategory && typeof newCategory.id === "number") {
            const entry = taskList[source.index];
            if (entry.type === "entry") {
                const ingredientName = entry.ingredient?.name || entry.task?.i?.name;
                if (ingredientName) {
                    categorizeIngredient(newCategory.id, ingredientName);
                }
            }
        }
        const newTaskList = reorder(taskList, source.index, destination.index);
        setTaskList(newTaskList);
    };

    const onBack = () => {
        if (selectedList) {
            setSelectedList(undefined);
            return;
        }

        onClose?.();
    };

    const onAdd = () => {
        if (!taskList || !selectedList || updating) return;

        const { newTasks, entries } = buildUpdates(taskList);
        setUpdating({ count: 0, total: newTasks.length });
        addToTickTickList(selectedList.tickTick.id, selectedList.list.id, newTasks)
            .then(async stream => {
                const reader = stream.getReader();
                let count = 0;
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const task = value.task;
                    if (task) {
                        setTaskList(taskList => {
                            if (!taskList) return taskList;

                            const copy = taskList.slice();
                            const taskListIndex = copy.findIndex(
                                t => t.type === "entry" && t.uniqueId === entries[value.index].uniqueId
                            );
                            if (taskListIndex !== -1) {
                                copy[taskListIndex] = {
                                    ...(copy[taskListIndex] as IngredientEntry),
                                    task: { t: task },
                                };
                            }

                            return copy;
                        });
                    }

                    ++count;
                    setUpdating(prev => ({ count, total: prev!.total }));
                }
            })
            .finally(() => {
                setUpdating(undefined);
            });
    };

    let content: ReactElement | ReactElement[];
    if (selectedList) {
        let innerContent: ReactElement | ReactElement[];
        if (taskList) {
            innerContent = (
                <DragDropContext onDragEnd={onDragEnd}>
                    <IngredientsToBuy entries={taskList}></IngredientsToBuy>
                </DragDropContext>
            );
        } else {
            innerContent = <CircularProgress />;
        }

        content = (
            <Stack direction="column">
                <Typography variant="h6">{selectedList.list.name}</Typography>
                <Divider></Divider>
                {innerContent}
            </Stack>
        );
    } else if (tickTick) {
        content = tickTick.map(t => {
            let backgroundColor: string | undefined;
            let innerContent: ReactElement | ReactElement[] | string;

            if (t.expired) {
                backgroundColor = "#ffcccc";
                innerContent = (
                    <Typography color="error">
                        Din inloggning till TickTick har gått ut, gå till inställningar för att logga in igen.
                    </Typography>
                );
            } else {
                innerContent = t.lists.map(l => {
                    return (
                        <Button
                            key={l.id}
                            style={{ color: l.color || "black" }}
                            variant="outlined"
                            onClick={() => onSelectList(t, l)}
                        >
                            {l.name}
                        </Button>
                    );
                });
            }

            return (
                <Paper key={"" + t.id} style={{ backgroundColor }}>
                    <Stack direction="column" spacing="15px" padding="15px">
                        <div>{t.name}</div>
                        {innerContent}
                    </Stack>
                </Paper>
            );
        });
    } else {
        content = <CircularProgress />;
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth={true}>
            <DialogTitle>Lägg till i TickTick</DialogTitle>
            <DialogContent>{content}</DialogContent>
            <DialogActions>
                <Button onClick={onBack}>{selectedList ? "Back" : "Close"}</Button>
                <PendingButton
                    onClick={onAdd}
                    disabled={!(selectedList && taskList)}
                    pending={!!updating}
                    variant="contained"
                >
                    Lägg till {updating ? `(${updating.count}/${updating.total})` : ""}
                </PendingButton>
            </DialogActions>
        </Dialog>
    );
}
